import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send, Wifi, Search, RotateCcw, ChevronDown } from 'lucide-react';
import { proxyApi, type ProxyResponse, type ProxyBroadcastResponse, type ConnectivityTestResponse } from '../../api/endpoints/proxy';
import { clientsApi } from '../../api/endpoints/clients';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageSpinner } from '../../components/ui/Spinner';
import { useDebounce } from '../../hooks/useDebounce';

type TargetType = 'client' | 'broadcast' | 'call';
type ResponseState = {
  status: 'idle' | 'sending' | 'success' | 'error';
  data?: ProxyResponse | ProxyBroadcastResponse | ConnectivityTestResponse | null;
  error?: string;
  latency?: number;
};

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;

export function ProxyPage() {
  const [targetType, setTargetType] = useState<TargetType>('client');
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [endpointPath, setEndpointPath] = useState('/');
  const [httpMethod, setHttpMethod] = useState('GET');
  const [requestHeaders, setRequestHeaders] = useState('{}');
  const [requestBody, setRequestBody] = useState('{}');
  const [timeout, setTimeout_] = useState(30000);
  const [deploymentId, setDeploymentId] = useState('');
  const [response, setResponse] = useState<ResponseState>({ status: 'idle' });

  const debouncedSearch = useDebounce(clientSearch);

  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['clients-for-proxy', debouncedSearch],
    queryFn: () => clientsApi.list({ page: 1, limit: 200, search: debouncedSearch }),
  });

  const clients = clientsData?.clients || [];
  const connectedClients = clients.filter(c => c.status === 'connected');

  const selectedClient = clients.find(c => c.client_id === clientId);

  function parseJson(str: string): unknown {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }

  async function handleSend() {
    // Validate JSON
    const headers = parseJson(requestHeaders);
    const body = parseJson(requestBody);
    if (headers === null) {
      setResponse({ status: 'error', error: 'Invalid JSON in headers' });
      return;
    }
    if (body === null && httpMethod !== 'GET') {
      setResponse({ status: 'error', error: 'Invalid JSON in body' });
      return;
    }

    setResponse({ status: 'sending' });
    const startTime = Date.now();

    try {
      let result: ProxyResponse | ProxyBroadcastResponse;

      if (targetType === 'client') {
        if (!clientId) {
          setResponse({ status: 'error', error: 'Please select a client' });
          return;
        }
        result = await proxyApi.forwardToClient(clientId, {
          path: endpointPath,
          method: httpMethod,
          headers: headers as Record<string, string>,
          body: body,
          timeout,
        });
      } else if (targetType === 'broadcast') {
        result = await proxyApi.broadcast({
          path: endpointPath,
          method: httpMethod,
          headers: headers as Record<string, string>,
          body: body,
          timeout,
        });
      } else {
        // call mode
        result = await proxyApi.call({
          client_id: clientId || undefined,
          deployment_id: deploymentId || undefined,
          endpoint: endpointPath,
          method: httpMethod,
          payload: body,
          timeout,
        });
      }

      const latency = Date.now() - startTime;
      setResponse({
        status: result.success ? 'success' : 'error',
        data: result,
        latency,
      });
    } catch (err) {
      setResponse({
        status: 'error',
        error: (err as Error).message,
        latency: Date.now() - startTime,
      });
    }
  }

  async function handleTestConnectivity(testClientId: string) {
    setResponse({ status: 'sending' });
    const startTime = Date.now();

    try {
      const result = await proxyApi.testClient(testClientId);
      setResponse({
        status: result.connectivity === 'OK' ? 'success' : 'error',
        data: result,
        latency: Date.now() - startTime,
      });
    } catch (err) {
      setResponse({
        status: 'error',
        error: (err as Error).message,
        latency: Date.now() - startTime,
      });
    }
  }

  function resetForm() {
    setClientId('');
    setClientSearch('');
    setEndpointPath('/');
    setHttpMethod('GET');
    setRequestHeaders('{}');
    setRequestBody('{}');
    setTimeout_(30000);
    setDeploymentId('');
    setResponse({ status: 'idle' });
  }

  const statusColor = {
    idle: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    sending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };

  const statusLabel = {
    idle: 'Waiting...',
    sending: 'Sending...',
    success: 'Success',
    error: 'Error',
  };

  if (isLoading && !clientsData) return <PageSpinner />;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Centralized API Proxy</h1>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Forward API requests to local servers through tunnel connections</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Request Builder */}
        <div className="space-y-3">
          <Card title="Request Builder">
            <div className="p-4 space-y-3">
              {/* Target Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Target Type</label>
                <select
                  value={targetType}
                  onChange={e => setTargetType(e.target.value as TargetType)}
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                >
                  <option value="client">Single Client</option>
                  <option value="broadcast">Broadcast (All Connected)</option>
                  <option value="call">Unified Call (by ID or Deployment)</option>
                </select>
              </div>

              {/* Client Selection */}
              {(targetType === 'client' || targetType === 'call') && (
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {targetType === 'call' ? 'Client ID (optional if using Deployment ID)' : 'Select Client'}
                  </label>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2 text-gray-400" />
                    <input
                      type="text"
                      value={clientSearch || (selectedClient ? (selectedClient.deployment_name || selectedClient.client_name) : '')}
                      onChange={e => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                      onFocus={() => setShowClientDropdown(true)}
                      placeholder="Search clients by name, deployment, ID..."
                      className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                    />
                    <ChevronDown size={14} className="absolute right-2.5 top-2 text-gray-400" />
                  </div>
                  {showClientDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#111] border border-gray-200 dark:border-gray-800 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {clients.map(c => (
                        <button
                          key={c.client_id}
                          onClick={() => {
                            setClientId(c.client_id);
                            setClientSearch('');
                            setShowClientDropdown(false);
                          }}
                          disabled={c.status !== 'connected'}
                          className="w-full text-left px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-gray-800 dark:text-gray-200">
                            {c.deployment_name || c.client_name}
                            {c.status !== 'connected' && <span className="text-gray-400 ml-1">- {c.status}</span>}
                          </span>
                          <span className={`w-2 h-2 rounded-full ${c.status === 'connected' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        </button>
                      ))}
                      {clients.length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No clients found</p>
                      )}
                    </div>
                  )}
                  {clientId && (
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="purple">{clientId}</Badge>
                      <button onClick={() => { setClientId(''); setClientSearch(''); }} className="text-gray-400 hover:text-gray-600">
                        <RotateCcw size={12} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Deployment ID (call mode only) */}
              {targetType === 'call' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Deployment ID (alternative to Client ID)</label>
                  <input
                    type="text"
                    value={deploymentId}
                    onChange={e => setDeploymentId(e.target.value)}
                    placeholder="DEPLOY-XXX"
                    className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs font-mono dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}

              {/* Endpoint Path */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Endpoint Path</label>
                <input
                  type="text"
                  value={endpointPath}
                  onChange={e => setEndpointPath(e.target.value)}
                  placeholder="/api/your-endpoint"
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs font-mono dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* HTTP Method */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">HTTP Method</label>
                <select
                  value={httpMethod}
                  onChange={e => setHttpMethod(e.target.value)}
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                >
                  {HTTP_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              {/* Headers */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Headers (JSON)</label>
                <textarea
                  value={requestHeaders}
                  onChange={e => setRequestHeaders(e.target.value)}
                  rows={2}
                  placeholder='{"Authorization": "Bearer token"}'
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs font-mono dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Request Body (JSON)</label>
                <textarea
                  value={requestBody}
                  onChange={e => setRequestBody(e.target.value)}
                  rows={6}
                  placeholder='{"key": "value"}'
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs font-mono dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Timeout */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Timeout (ms)</label>
                <input
                  type="number"
                  value={timeout}
                  onChange={e => setTimeout_(Number(e.target.value))}
                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleSend}
                  disabled={response.status === 'sending'}
                  className="flex-1 bg-purple-600 text-white py-2.5 rounded-md hover:bg-purple-700 transition-colors font-medium text-xs flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Send size={14} />
                  {response.status === 'sending' ? 'Sending...' : 'Send Request'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <RotateCcw size={14} />
                </button>
              </div>
            </div>
          </Card>

          {/* API Docs */}
          <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4">
            <h3 className="text-white font-bold text-sm mb-3">External API Usage</h3>
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-gray-400 mb-0.5">Unified Endpoint:</p>
                <code className="text-green-400">POST /api/proxy/call</code>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Headers:</p>
                <code className="text-green-400">Authorization: Bearer YOUR_API_KEY</code>
              </div>
              <div>
                <p className="text-gray-400 mb-0.5">Body:</p>
                <pre className="text-green-400 text-[11px] overflow-x-auto">{`{
  "deployment_id": "DEPLOY-XXX",
  "endpoint": "/api/your-endpoint",
  "method": "POST",
  "payload": { "data": "value" }
}`}</pre>
              </div>
            </div>
          </div>
        </div>

        {/* Response Panel */}
        <div className="space-y-3">
          <Card title="Response" action={
            <div className="flex items-center gap-2">
              {response.latency != null && (
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{response.latency}ms</span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[response.status]}`}>
                {statusLabel[response.status]}
              </span>
            </div>
          }>
            <div className="bg-gray-900 dark:bg-gray-950 rounded-b-lg p-4 min-h-[400px] max-h-[600px] overflow-auto">
              <pre className="text-green-400 font-mono text-xs whitespace-pre-wrap">
                {response.status === 'idle' && '// Response will appear here'}
                {response.status === 'sending' && 'Sending request...'}
                {response.error && !response.data && `Error: ${response.error}`}
                {response.data && JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          </Card>

          {/* Quick Connectivity Test */}
          <Card title="Quick Connectivity Test">
            <div className="p-4 space-y-2">
              {connectedClients.length > 0 ? (
                connectedClients.slice(0, 10).map(c => (
                  <div key={c.client_id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                      <div>
                        <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{c.deployment_name || c.client_name}</p>
                        <p className="text-[10px] text-gray-400">{c.hostname || c.local_ip || c.client_id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleTestConnectivity(c.client_id)}
                      disabled={response.status === 'sending'}
                      className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs hover:bg-blue-200 dark:hover:bg-blue-900/50 flex items-center gap-1 disabled:opacity-50"
                    >
                      <Wifi size={12} />
                      Test
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-3">No connected clients</p>
              )}
              {connectedClients.length > 10 && (
                <p className="text-center text-[10px] text-gray-400">Showing first 10 of {connectedClients.length} connected clients</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
